import React, { useState, useEffect, useRef } from 'react';
import styled, { keyframes } from 'styled-components';
import useAuthenticatedAxios from "../../../auth/AuthenticatedAxios";
import { useAuth0 } from '@auth0/auth0-react';
import DOMPurify from 'dompurify';
import ImavenLoadingScreen from '../../../Components/Helpers/loading-screen';
import ChatBotIcon from '../../../Images/chat-bot-icon.svg';
import UserChatIcon from '../../../Images/user-chat-icon.svg';
import { useAIAssistant } from '../../../Components/AIAssistant';

// ============================================
// SVG ICONS (Simple, monochrome style)
// ============================================

const Icons = {
  document: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/><polyline points="10 9 9 9 8 9"/></svg>,
  beaker: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M9 3h6v5l4 9a2 2 0 0 1-1.8 2.9H6.8A2 2 0 0 1 5 17l4-9V3z"/><path d="M9 3h6"/></svg>,
};

// ============================================
// ANIMATIONS
// ============================================

const bounce = keyframes`
  0%, 80%, 100% { transform: translateY(0); }
  40% { transform: translateY(-5px); }
`;

// Loading dots animation component
const LoadingDots = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 3px;
  
  span {
    width: 5px;
    height: 5px;
    background: currentColor;
    border-radius: 50%;
    animation: ${bounce} 1s infinite;
    
    &:nth-child(1) { animation-delay: 0s; }
    &:nth-child(2) { animation-delay: 0.15s; }
    &:nth-child(3) { animation-delay: 0.3s; }
  }
`;

// ============================================
// STYLED COMPONENTS
// ============================================

const ModuleContainer = styled.div`
  display: flex;
  height: 100%;
  width: 100%;
  gap: 20px;
  padding: 20px;
  box-sizing: border-box;
`;

const LeftPanel = styled.div`
  width: 50%;
  height: 100%;
  display: flex;
  flex-direction: column;
  background: rgba(20, 25, 40, 0.6);
  border: 1px solid rgba(99, 102, 241, 0.15);
  border-radius: 16px;
  overflow: hidden;
`;

const RightPanel = styled.div`
  width: 50%;
  height: 100%;
  display: flex;
  flex-direction: column;
  background: rgba(20, 25, 40, 0.6);
  border: 1px solid rgba(99, 102, 241, 0.15);
  border-radius: 16px;
  overflow: hidden;
`;

const PanelHeader = styled.div`
  padding: 16px 20px;
  border-bottom: 1px solid rgba(99, 102, 241, 0.15);
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const PanelTitle = styled.h3`
  font-size: 16px;
  font-weight: 600;
  color: #a5b4fc;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const AgentSelector = styled.select`
  background: rgba(99, 102, 241, 0.1);
  border: 1px solid rgba(99, 102, 241, 0.3);
  border-radius: 8px;
  padding: 8px 12px;
  color: #a5b4fc;
  font-size: 13px;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    border-color: rgba(99, 102, 241, 0.5);
  }

  &:focus {
    outline: none;
    border-color: #6366f1;
  }

  option {
    background: #1a1f2e;
  }
`;

const DirectiveDisplay = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 20px;
  background: rgba(15, 20, 30, 0.5);
  
  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background: rgba(99, 102, 241, 0.3);
    border-radius: 4px;
  }

  .directive-content {
    line-height: 1.7;
    color: rgba(255, 255, 255, 0.85);
    
    h1, h2, h3, h4 {
      color: #a5b4fc;
      margin: 16px 0 10px 0;
    }
    
    ul {
      padding-left: 20px;
      margin: 12px 0;
    }
    
    li {
      margin-bottom: 8px;
    }
  }
`;

const RequirementsArea = styled.div`
  padding: 16px 20px;
  border-top: 1px solid rgba(99, 102, 241, 0.15);
  background: rgba(15, 20, 30, 0.3);
`;

const RequirementsLabel = styled.label`
  font-size: 12px;
  color: rgba(165, 180, 252, 0.7);
  margin-bottom: 8px;
  display: block;
`;

const RequirementsInput = styled.textarea`
  width: 100%;
  height: 120px;
  background: rgba(25, 30, 45, 0.8);
  border: 1px solid rgba(99, 102, 241, 0.2);
  border-radius: 10px;
  padding: 12px;
  color: rgba(255, 255, 255, 0.9);
  font-size: 13px;
  resize: none;
  font-family: inherit;

  &:focus {
    outline: none;
    border-color: rgba(99, 102, 241, 0.5);
  }

  &::placeholder {
    color: rgba(255, 255, 255, 0.4);
  }
`;

const ButtonRow = styled.div`
  display: flex;
  gap: 10px;
  margin-top: 12px;
`;

const Button = styled.button`
  flex: ${props => props.compact ? '0 0 auto' : '1'};
  padding: ${props => props.compact ? '8px 16px' : '10px 16px'};
  border-radius: 8px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  
  ${props => props.primary ? `
    background: linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%);
    border: none;
    color: #fff;
    
    &:hover {
      box-shadow: 0 4px 15px rgba(99, 102, 241, 0.4);
    }
  ` : `
    background: rgba(99, 102, 241, 0.1);
    border: 1px solid rgba(99, 102, 241, 0.3);
    color: #a5b4fc;
    
    &:hover {
      background: rgba(99, 102, 241, 0.15);
    }
  `}

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

// Chat Components
const ChatArea = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const ChatMessages = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 20px;
  
  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background: rgba(99, 102, 241, 0.3);
    border-radius: 4px;
  }
`;

const MessageBubble = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  margin-bottom: 16px;
  flex-direction: ${props => props.isUser ? 'row-reverse' : 'row'};
`;

const MessageIcon = styled.img`
  width: 28px;
  height: 28px;
  border-radius: 8px;
`;

const Message = styled.div`
  max-width: 75%;
  padding: 12px 16px;
  border-radius: 12px;
  font-size: 14px;
  line-height: 1.5;
  
  ${props => props.isUser ? `
    background: rgba(99, 102, 241, 0.2);
    border: 1px solid rgba(99, 102, 241, 0.3);
    color: #e8eaed;
  ` : `
    background: rgba(30, 35, 50, 0.8);
    border: 1px solid rgba(100, 150, 200, 0.1);
    color: rgba(255, 255, 255, 0.9);
  `}
`;

const ChatInputArea = styled.div`
  padding: 16px 20px;
  border-top: 1px solid rgba(99, 102, 241, 0.15);
  display: flex;
  gap: 10px;
`;

const ChatInput = styled.input`
  flex: 1;
  background: rgba(25, 30, 45, 0.8);
  border: 1px solid rgba(99, 102, 241, 0.2);
  border-radius: 10px;
  padding: 12px 16px;
  color: #fff;
  font-size: 14px;

  &:focus {
    outline: none;
    border-color: rgba(99, 102, 241, 0.5);
  }

  &::placeholder {
    color: rgba(255, 255, 255, 0.4);
  }
`;

const SendButton = styled(Button)`
  flex: none;
  width: auto;
  padding: 12px 24px;
`;

// Observer Components
const ObserverInput = styled.div`
  padding: 20px;
  display: flex;
  gap: 10px;
`;

const ObserverInsights = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 20px;
  
  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background: rgba(99, 102, 241, 0.3);
    border-radius: 4px;
  }
`;

const InsightCard = styled.div`
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-left: 4px solid ${props => {
    switch(props.priority?.toLowerCase()) {
      case 'high': return '#ff4444';
      case 'medium': return '#ffbb33';
      case 'low': return '#00C851';
      default: return '#00C851';
    }
  }};
  border-radius: 8px;
  padding: 12px;
  margin-bottom: 12px;
  font-size: 13px;
  
  .timestamp {
    font-size: 10px;
    color: rgba(255, 255, 255, 0.4);
    margin-top: 8px;
    text-align: right;
  }
`;

// Summarizer Components
const SummarizerLayout = styled.div`
  display: flex;
  flex: 1;
  gap: 16px;
  padding: 20px;
  overflow: hidden;
`;

const SummarizerInput = styled.div`
  width: 40%;
  display: flex;
  flex-direction: column;
`;

const SummarizerOutput = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
`;

const TextArea = styled.textarea`
  flex: 1;
  background: rgba(25, 30, 45, 0.8);
  border: 1px solid rgba(99, 102, 241, 0.2);
  border-radius: 10px;
  padding: 12px;
  color: rgba(255, 255, 255, 0.9);
  font-size: 13px;
  resize: none;
  font-family: inherit;

  &:focus {
    outline: none;
    border-color: rgba(99, 102, 241, 0.5);
  }

  &::placeholder {
    color: rgba(255, 255, 255, 0.4);
  }
`;

const OutputDisplay = styled.div`
  flex: 1;
  background: rgba(25, 30, 45, 0.8);
  border: 1px solid rgba(99, 102, 241, 0.2);
  border-radius: 10px;
  padding: 16px;
  overflow-y: auto;
  color: rgba(200, 220, 240, 0.9);
  font-size: 14px;
  line-height: 1.6;
  
  &:empty:before {
    content: 'Summary will appear here...';
    color: rgba(255, 255, 255, 0.3);
    font-style: italic;
  }
`;

const Label = styled.label`
  font-size: 12px;
  color: rgba(165, 180, 252, 0.7);
  margin-bottom: 8px;
  display: block;
`;

const InfoBox = styled.div`
  background: rgba(66, 135, 245, 0.1);
  border-left: 3px solid rgba(66, 135, 245, 0.6);
  border-radius: 8px;
  padding: 12px 16px;
  margin-bottom: 16px;
  font-size: 13px;
  color: rgba(200, 220, 240, 0.9);
  
  strong {
    color: rgba(66, 135, 245, 0.9);
  }
`;

// ============================================
// MAIN COMPONENT
// ============================================

const AgentDirectivesModule = () => {
  useAuth0(); // Keep hook call for auth context
  const authenticatedAxios = useAuthenticatedAxios();
  
  // AI Assistant context
  const { updateContext, updateFormState } = useAIAssistant();

  // State
  const [selectedAgentType, setSelectedAgentType] = useState('widget');
  const [generatedDirective, setGeneratedDirective] = useState('');
  const [requirements, setRequirements] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  
  // Widget chat state
  const [chatHistory, setChatHistory] = useState([]);
  const [chatInput, setChatInput] = useState('');
  const [isMessageSending, setIsMessageSending] = useState(false);
  
  // Observer state
  const [observerInput, setObserverInput] = useState('');
  const [observerInsights, setObserverInsights] = useState([]);
  const [isObserverLoading, setIsObserverLoading] = useState(false);
  
  // Summarizer state
  const [summarizerInput, setSummarizerInput] = useState('');
  const [summarizerOutput, setSummarizerOutput] = useState('');
  const [isSummarizing, setIsSummarizing] = useState(false);

  const messagesEndRef = useRef(null);

  const startWidgetTestChat = async () => {
    try {
      localStorage.removeItem('threadId');
      localStorage.removeItem('conversationId');

      const response = await authenticatedAxios.post('/synthora/startChat');
      const conversationId = response.data.conversationId || response.data.convesationId;
      if (conversationId) {
        localStorage.setItem('conversationId', conversationId);
      }

      setChatHistory([{
        text: "Welcome! Test your Widget Agent by typing a message below.",
        isUser: false,
        icon: ChatBotIcon
      }]);
      setChatInput('');
    } catch (error) {
      console.error('Error starting chat:', error);
      setChatHistory([{
        text: "Unable to start a fresh test chat right now. Please try again.",
        isUser: false,
        icon: ChatBotIcon
      }]);
    }
  };

  const resetTestArea = async (agentType = selectedAgentType) => {
    if (agentType === 'widget') {
      await startWidgetTestChat();
      return;
    }

    if (agentType === 'observer') {
      setObserverInput('');
      setObserverInsights([]);
      return;
    }

    if (agentType === 'summarizer') {
      setSummarizerInput('');
      setSummarizerOutput('');
    }
  };

  // Update AI Assistant context when state changes
  useEffect(() => {
    updateContext({
      currentModule: 'agent-directives',
      moduleName: 'Agent Directives',
      moduleDescription: 'Configure AI agent behavior',
      selectedElements: {
        agentType: selectedAgentType,
        hasDirective: !!generatedDirective && generatedDirective.length > 50,
      },
      availableActions: [
        'generateDirective',
        'updateDirective', 
        'testAgent',
        'switchAgentType'
      ],
      uiState: {
        selectedAgentType,
        isProcessing,
        chatMessageCount: chatHistory.length,
      }
    });
  }, [selectedAgentType, generatedDirective, isProcessing, chatHistory.length, updateContext]);

  // Update form state when requirements change
  useEffect(() => {
    updateFormState({
      requirements: requirements,
      hasRequirements: requirements.trim().length > 0
    });
  }, [requirements, updateFormState]);

  // Helper to clean markdown
  const cleanMarkdownCodeBlocks = (content) => {
    if (!content) return '';
    return content.replace(/```(html|javascript|css|jsx|json|xml|plaintext|bash|shell)?\n/g, '').replace(/```/g, '');
  };

  // Load directive on agent type change
  useEffect(() => {
    const loadDirective = async () => {
      setGeneratedDirective('');
      try {
        const response = await authenticatedAxios.get(`/synthora/getDirective?agentType=${selectedAgentType}`);
        if (response.data?.data) {
          setGeneratedDirective(response.data.data);
        } else {
          const agentName = selectedAgentType === 'widget' ? 'Widget' : 
                           selectedAgentType === 'observer' ? 'Observer' : 'Summarizer';
          setGeneratedDirective(`# Welcome to ${agentName} Agent Setup\n\nNo directive created yet. Write requirements below and click "Generate" to create one.`);
        }
      } catch (error) {
        console.error('Error loading directive:', error);
        setGeneratedDirective('# Error Loading Directive\n\nPlease try refreshing the page.');
      }
    };

    loadDirective();
    if (selectedAgentType === 'widget') {
      startWidgetTestChat();
    } else if (selectedAgentType === 'observer') {
      setObserverInput('');
      setObserverInsights([]);
    } else if (selectedAgentType === 'summarizer') {
      setSummarizerInput('');
      setSummarizerOutput('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedAgentType]);

  // Scroll to bottom of chat
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [chatHistory]);

  // Handle generate directive
  const handleGenerateDirective = async () => {
    if (!requirements.trim() || isProcessing) return;
    setIsProcessing(true);
    setGeneratedDirective('');
    
    try {
      const response = await authenticatedAxios.post('/synthora/generateDirective', {
        requirements,
        agentType: selectedAgentType
      });
      setGeneratedDirective(response.data.directive);
      await resetTestArea(selectedAgentType);
    } catch (error) {
      console.error('Error generating directive:', error);
      setGeneratedDirective('# Error\n\nFailed to generate directive. Please try again.');
    } finally {
      setIsProcessing(false);
    }
  };

  // Handle update directive
  const handleUpdateDirective = async () => {
    if (!requirements.trim() || isProcessing) return;
    setIsProcessing(true);
    
    try {
      const response = await authenticatedAxios.post('/synthora/updateDirective', {
        requirements,
        agentType: selectedAgentType
      });
      setGeneratedDirective(response.data.message);
      await resetTestArea(selectedAgentType);
    } catch (error) {
      console.error('Error updating directive:', error);
    } finally {
      setIsProcessing(false);
    }
  };

  // Handle chat message
  const handleSendMessage = async () => {
    if (!chatInput.trim() || isMessageSending) return;

    const userMessage = { text: chatInput, isUser: true, icon: UserChatIcon };
    setChatHistory(prev => [...prev, userMessage]);
    setIsMessageSending(true);
    setChatInput('');

    try {
      const conversationId = localStorage.getItem('conversationId');
      const threadId = localStorage.getItem('threadId') || 'none';

      const response = await authenticatedAxios.post('/synthora/chat', {
        chatInput: userMessage.text,
        threadId,
        conversationId
      });

      if (response.data?.threadId) {
        localStorage.setItem('threadId', response.data.threadId);
      }

      setChatHistory(prev => [...prev, {
        text: response.data?.message || "Sorry, I couldn't process that.",
        isUser: false,
        icon: ChatBotIcon
      }]);
    } catch (error) {
      console.error('Error sending message:', error);
      setChatHistory(prev => [...prev, {
        text: "Sorry, there was an error. Please try again.",
        isUser: false,
        icon: ChatBotIcon
      }]);
    } finally {
      setIsMessageSending(false);
    }
  };

  // Handle observer test
  const handleTestObserver = async () => {
    if (!observerInput.trim() || isObserverLoading) return;
    setIsObserverLoading(true);
    
    try {
      const response = await authenticatedAxios.post('/synthora/testObserver', {
        messages: [{ type: 'user', text: observerInput }],
        agentType: 'observer'
      });
      
      let processed = response.data.data;
      if (typeof processed === 'string') {
        processed = processed.replace(/^```html\n/, '').replace(/\n```$/, '');
      }
      
      setObserverInsights(prev => [{
        html: processed,
        priority: 'medium',
        timestamp: new Date().toLocaleTimeString()
      }, ...prev].slice(0, 5));
      
      setObserverInput('');
    } catch (error) {
      console.error('Error testing observer:', error);
    } finally {
      setIsObserverLoading(false);
    }
  };

  // Handle summarizer test
  const handleTestSummarizer = async () => {
    if (!summarizerInput.trim() || isSummarizing) return;
    setIsSummarizing(true);
    setSummarizerOutput('');
    
    try {
      const response = await authenticatedAxios.post('/synthora/testSummarizer', {
        conversation: summarizerInput,
        agentType: 'summarizer'
      });
      setSummarizerOutput(response.data.summary);
    } catch (error) {
      console.error('Error testing summarizer:', error);
      setSummarizerOutput('Error: Unable to generate summary.');
    } finally {
      setIsSummarizing(false);
    }
  };

  return (
    <ModuleContainer>
      {/* Left Panel - Directive */}
      <LeftPanel>
        <PanelHeader>
          <PanelTitle>{Icons.document} Current Directive</PanelTitle>
          <AgentSelector 
            value={selectedAgentType}
            onChange={(e) => setSelectedAgentType(e.target.value)}
          >
            <option value="widget">Widget Agent</option>
            <option value="observer">Observer Agent</option>
            <option value="summarizer">Summarizer Agent</option>
          </AgentSelector>
        </PanelHeader>

        <DirectiveDisplay>
          {generatedDirective ? (
            <div 
              className="directive-content"
              dangerouslySetInnerHTML={{ 
                __html: DOMPurify.sanitize(cleanMarkdownCodeBlocks(generatedDirective)) 
              }} 
            />
          ) : (
            <ImavenLoadingScreen />
          )}
        </DirectiveDisplay>

        <RequirementsArea>
          <RequirementsLabel>Update Requirements</RequirementsLabel>
          <RequirementsInput
            value={requirements}
            onChange={(e) => setRequirements(e.target.value)}
            placeholder="Describe what you want your agent to do..."
          />
          <ButtonRow>
            <Button 
              onClick={handleUpdateDirective}
              disabled={isProcessing || !requirements.trim()}
            >
              {isProcessing ? 'Updating...' : 'Update'}
            </Button>
            <Button 
              primary
              onClick={handleGenerateDirective}
              disabled={isProcessing || !requirements.trim()}
            >
              {isProcessing ? 'Generating...' : 'Generate New'}
            </Button>
          </ButtonRow>
        </RequirementsArea>
      </LeftPanel>

      {/* Right Panel - Testing */}
      <RightPanel>
        <PanelHeader>
          <PanelTitle>
            {Icons.beaker} Test {selectedAgentType === 'widget' ? 'Widget' : 
                     selectedAgentType === 'observer' ? 'Observer' : 'Summarizer'} Agent
          </PanelTitle>
        </PanelHeader>

        {/* Widget Agent Test */}
        {selectedAgentType === 'widget' && (
          <>
            <ChatArea>
              <ChatMessages>
                {chatHistory.map((msg, idx) => (
                  <MessageBubble key={idx} isUser={msg.isUser}>
                    <MessageIcon src={msg.icon} alt="" />
                    <Message 
                      isUser={msg.isUser}
                      dangerouslySetInnerHTML={{ 
                        __html: DOMPurify.sanitize(msg.isUser ? msg.text : cleanMarkdownCodeBlocks(msg.text)) 
                      }}
                    />
                  </MessageBubble>
                ))}
                <div ref={messagesEndRef} />
              </ChatMessages>
            </ChatArea>
            <ChatInputArea>
              <Button compact onClick={() => resetTestArea('widget')} disabled={isMessageSending}>
                Clear Chat
              </Button>
              <ChatInput
                value={chatInput}
                onChange={(e) => setChatInput(e.target.value)}
                placeholder="Type a message to test..."
                onKeyPress={(e) => e.key === 'Enter' && handleSendMessage()}
                disabled={isMessageSending}
              />
              <SendButton primary onClick={handleSendMessage} disabled={isMessageSending}>
                {isMessageSending ? <LoadingDots><span></span><span></span><span></span></LoadingDots> : 'Send'}
              </SendButton>
            </ChatInputArea>
          </>
        )}

        {/* Observer Agent Test */}
        {selectedAgentType === 'observer' && (
          <>
            <InfoBox>
              <strong>How to Test:</strong> Enter a user message and click Analyze. 
              The Observer will provide real-time insights.
            </InfoBox>
            <ObserverInput>
              <ChatInput
                value={observerInput}
                onChange={(e) => setObserverInput(e.target.value)}
                placeholder="Enter user message to analyze..."
                onKeyPress={(e) => e.key === 'Enter' && handleTestObserver()}
                disabled={isObserverLoading}
                style={{ flex: 1 }}
              />
              <Button 
                primary 
                onClick={handleTestObserver}
                disabled={isObserverLoading || !observerInput.trim()}
                style={{ width: 'auto', padding: '12px 20px' }}
              >
                {isObserverLoading ? 'Analyzing...' : 'Analyze'}
              </Button>
            </ObserverInput>
            <ObserverInsights>
              {observerInsights.length === 0 && (
                <div style={{ textAlign: 'center', color: 'rgba(255,255,255,0.3)', padding: '40px' }}>
                  Observer insights will appear here...
                </div>
              )}
              {observerInsights.map((insight, idx) => (
                <InsightCard key={idx} priority={insight.priority}>
                  <div dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(insight.html) }} />
                  <div className="timestamp">{insight.timestamp}</div>
                </InsightCard>
              ))}
            </ObserverInsights>
          </>
        )}

        {/* Summarizer Agent Test */}
        {selectedAgentType === 'summarizer' && (
          <SummarizerLayout>
            <SummarizerInput>
              <Label>Conversation Input:</Label>
              <TextArea
                value={summarizerInput}
                onChange={(e) => setSummarizerInput(e.target.value)}
                placeholder="Paste conversation here..."
              />
              <Button 
                primary 
                compact
                onClick={handleTestSummarizer}
                disabled={isSummarizing || !summarizerInput.trim()}
                style={{ marginTop: '12px', alignSelf: 'flex-start' }}
              >
                {isSummarizing ? <><LoadingDots><span></span><span></span><span></span></LoadingDots> Summarizing</> : 'Generate Summary'}
              </Button>
            </SummarizerInput>
            <SummarizerOutput>
              <Label>Generated Summary:</Label>
              <OutputDisplay>
                {summarizerOutput && (
                  <div dangerouslySetInnerHTML={{ 
                    __html: DOMPurify.sanitize(cleanMarkdownCodeBlocks(summarizerOutput)) 
                  }} />
                )}
              </OutputDisplay>
            </SummarizerOutput>
          </SummarizerLayout>
        )}
      </RightPanel>
    </ModuleContainer>
  );
};

export default AgentDirectivesModule;


